<template>
  <div id="wrapper">

    <div style="margin-bottom: 10px;" v-if="canCreateTask">
      <button class="btn btn-primary text-white" @click="openCreateTaskPopup">
        + Hitro opravilo
      </button>
    </div>

    <div class="user-filter" v-if="canManageFilters">
      <div
          :class="{ selected: selectedUserIds.length === 0 }"
          @click="showAllUsers"
          class="user-filter-item"
      >
        Pokaži vse delavce
      </div>
      <div
          v-for="user in usersData"
          :key="user.id"
          :class="['user-filter-item', { selected: selectedUserIds.includes(user.id) }]"
          @click="toggleUserFilter(user.id)"
      >
        {{ user.text }}
      </div>
    </div>


    <DxScheduler
        id="scheduler"
        ref="scheduler"
        :data-source="dataSource"
        :current-date="currentDate"
        :views="views"
        :max-appointments-per-cell="'unlimited'"
        :cell-duration="schedulerCellDuration"
        :groups="groups"
        :time-zone="'Europe/Ljubljana'"
        :start-day-hour="schedulerDefaultStartDayHour"
        :end-day-hour="schedulerDefaultEndDayHour"
        :first-day-of-week="1"
        :show-current-time-indicator="true"
        current-view="timelineWeek"
        :current-view.sync="currentView"
        text-expr="name"
        start-date-expr="started_at"
        end-date-expr="completed_at"
        appointment-min-size="100"
        :editing="editingOptions"
        :on-appointment-form-opening="onAppointmentFormOpening"
        :on-option-changed="onOptionChanged"
        :on-appointment-context-menu="onAppointmentContextMenu"
        appointment-template="AppointmentTemplateSlot"
        @cell-click="onCellClick"
        @content-ready="onContentReady"
    >

      <template #dataCellTemplate="{ data: cellData }">
        <DataCell
            :cell-data="cellData"
            :non-working-cells="nonWorkingCellsCache"
            :end-day-hour="appliedEndHour ? appliedEndHour : schedulerDefaultEndDayHour"
            :cell-duration-minutes="schedulerCellDuration"
        />
      </template>

      <template #AppointmentTemplateSlot="{ data }">
        <div class="dx-item-content dx-scheduler-appointment-content"
             :class="data.appointmentData.completed ? '' : 'incomplete-task'"
             :id="'task-' + data.appointmentData.id"
             :data-id="data.appointmentData.id"
             :data-work-process-id="data.appointmentData.work_processes_id"
             :data-color-code="getTaskColor(data.appointmentData.id)"
             :style="{ 'background-color': getTaskColor(data.appointmentData.id) }"
             :title="data.appointmentData.display_text"
        >
          <div class="dx-scheduler-appointment-title">
            <CIcon name="cil-lock-locked" v-if="data.appointmentData.is_time_locked" style="width: 10px; height: 10px;" title="Zaklenjen čas"/>
            {{ data.appointmentData.display_text }}
          </div>
          <div class="dx-scheduler-appointment-content-details">
            <div class="dx-scheduler-appointment-content-date">
              {{ formatTime(data.appointmentData.started_at) }}
              -
              {{ formatTime(data.appointmentData.completed_at) }}
            </div>
          </div>
        </div>
      </template>
      <DxResource
          :allow-multiple="false"
          :data-source="filteredUsersData"
          field-expr="default_user_id"
          label="Zaposleni"
      />
      <DxAppointmentDragging
          v-if="canEditTask"
          :group="draggingGroupName"
          :on-add="onAppointmentAdd"
      />
      <svg ref="connectionsSvg" class="connections-svg"></svg>
    </DxScheduler>
    <DxContextMenu
        ref="contextMenu"
        :items="contextMenuItems"
        :width="200"
        :target="contextMenuTarget"
        :on-item-click="onContextMenuItemClick"
    >
    </DxContextMenu>
    <vue-toastr ref="toastr"></vue-toastr>
    <div id="pending-tasks-wrap">
      <h4>Čakajoče naloge</h4>
      <DxDraggable
          id="list"
          :group="draggingGroupName"
          :on-drag-start="onListDragStart"
          data="dropArea"
      >
        <div v-if="pendingTasks.length === 0" class="no-pending-tasks-message">
          Ni najdenih čakajočih nalog.
        </div>
        <DxDraggable
            v-for="pendingTask in pendingTasks"
            :key="pendingTask.id"
            :clone="true"
            :group="draggingGroupName"
            :data="pendingTask"
            :on-drag-start="onItemDragStart"
            :on-drag-end="onItemDragEnd"
            class="item dx-card"
        >
          <button
              v-if="canDeleteTask"
              @click="deletePendingTask(pendingTask)"
              class="delete-pending-task-button">&times;</button>
          <button
              v-if="canEditTask"
              @click="editPendingTask(pendingTask)"
              class="edit-pending-task-button"
              title="Uredi">&#9998;</button>
          <strong>{{ pendingTask.name }}</strong>
          Delovni process : {{ pendingTask.work_process && pendingTask.work_process.name ? pendingTask.work_process.name : '' }}
          <br>
          Naročilo: {{ pendingTask.sp_orders_data[0] ? pendingTask.sp_orders_data[0].number : '/' }}
          <br>
          Stranka: {{ pendingTask.sp_orders_data[0] ? pendingTask.sp_orders_data[0].client_name : '/' }}
          <br>
          Vozilo: {{ pendingTask.sp_orders_data[0] ? pendingTask.sp_orders_data[0].vehicle_data : '/' }}
          <br>
          Predvideno trajanje: {{ pendingTask.time }}h
        </DxDraggable>
      </DxDraggable>
    </div>

    <!-- Urgent Task Modal -->
    <CModal
        :title="urgentTaskData.id ? 'Uredi nalogo' : 'Hitro opravilo'"
        :show.sync="showUrgentTaskModal"
        size="lg"
        :close-on-backdrop="false"
    >
      <TaskForm
          :task="urgentTaskData"
          :show-task-modal="showUrgentTaskModal"
          form-type="extended"
          @submit="handleTaskSubmit"
          @cancel="closeUrgentTaskModal"
      />
      <template #footer>
        <CButton type="submit" color="primary" form="taskForm">Shrani</CButton>
        <CButton color="secondary" @click="closeUrgentTaskModal">Prekliči</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import TaskForm from "@/views/components/TaskForm.vue";
import DxDraggable from 'devextreme-vue/draggable';
import DxScrollView from 'devextreme-vue/scroll-view';
import DataCell from "@/views/components/WorkUtilizationDataCell.vue";
import {DxScheduler, DxResource, DxAppointmentDragging} from 'devextreme-vue/scheduler';
import {DxContextMenu} from 'devextreme-vue/context-menu';
import UsersService from "@/services/users";
import SchedulerService from "@/services/scheduler";
import axios from "axios";
import CustomStore from "devextreme/data/custom_store";
import {locale, loadMessages} from "devextreme/localization";
import { actions } from '@/store/actions/workprocess/actions'
import {format} from 'date-fns'
import {mapActions, mapGetters} from "vuex";
import settings from '@/settings';
import PermissionCheck from '@/tools/permission-check';

export default {
  name: 'TasksScheduler',
  components: {
    DxScheduler,
    DxResource,
    DxContextMenu,
    DxAppointmentDragging,
    DxDraggable,
    DxScrollView,
    DataCell,
    TaskForm
  },
  data: function () {
    return {
      views: ['timelineDay', 'timelineWeek'],
      groups: ['default_user_id'],
      currentDate: new Date(),
      currentView: 'timelineWeek',
      pendingTasks: [],
      scheduledTasks: [],
      dataSource: new CustomStore({
        key: "id",
        load: async () => {
          try {
            const dateFrom = this.formatDate(this.$refs.scheduler.instance.getStartViewDate());
            const dateTo = this.formatDate(this.$refs.scheduler.instance.getEndViewDate());

            const params = {
              date_from: dateFrom + ' 00:00:00',
              date_to: dateTo + ' 23:59:59',
            };

            if (this.selectedUserIds.length > 0) {
              params.user_ids = this.selectedUserIds;
            }

            // Fetch working time setups
            try {
              if (this.usersData.length > 0) {
                const timesheetParams = {
                  start_date: dateFrom,
                  end_date: dateTo,
                  user_ids: this.usersData.map(user => user.id)
                };
                const timesheetResponse = await SchedulerService.getTimesheetForPeriod(timesheetParams);
                this.timesheetData = timesheetResponse.timesheet || [];
                this.perDayWorkingHours = timesheetResponse.perDayWorkingHours || {};

                this.buildNonWorkingCellsCache();

                this.updateSchedulerHours();
              } else {
                this.timesheetData = [];
                this.perDayWorkingHours = {};
              }
            } catch (timesheetError) {
              this.timesheetData = [];
              this.perDayWorkingHours = {};
            }

            const response = await axios.get("/tasks/filter", { params });
            this.scheduledTasks = response.data.tasks.map(task => this.parseTask(task));

            this.buildTaskIndexes();
            this.pendingConnectionsDraw = true;

            return this.scheduledTasks;
          } catch (error) {
            console.error("Error loading events:", error);
            this.$refs.toastr.e(error?.response?.data?.error || error.message || "Med nalaganjem nalog je prišlo do napake.");
            throw error;
          }
        },
        insert: async (values) => {
          try {
            if (!values.name) {
              this.$refs?.toastr?.e('Naziv opravila je obvezen.');
              throw new Error('name is required');
            }

            const payload = {};

            payload.name = values.name;
            payload.started_at = this.formatDatetime(values.started_at);
            payload.completed_at = this.formatDatetime(values.completed_at);

            const start = new Date(values.started_at);
            const end = new Date(values.completed_at);
            const diffHours = Math.max(0.25, (end.getTime() - start.getTime()) / (1000 * 60 * 60));

            payload.time = +diffHours.toFixed(2);

            if (values.description != null) payload.description = values.description;
            if (values.default_user_id != null) payload.default_user_id = values.default_user_id;

            const created = await SchedulerService.createQuickTask({
              data: payload,
            });
            await this.onScheduleSuccess();

            return created;

          } catch (error) {
            console.error('Error creating task:', error);
            if (!error.silenced) {
              this.$refs?.toastr?.e(error?.response?.data?.error || error.message || 'Napaka pri ustvarjanju opravila.');
            }
            throw error;
          }
        },
        update: async (key, values) => {
          await this.handleSchedulingAttempt({
            initialData: values,
            taskId: key,
            onFailure: (err) => { throw err; }
          });
        },
      }),
      usersData: [],
      selectedUserIds: [],
      contextMenuAppointmentClassName: '.dx-scheduler-appointment',
      contextMenuItems: [],
      contextMenuTarget: '.dx-scheduler-appointment',
      draggingGroupName: 'appointmentsGroup',
      timesheetData: [],
      perDayWorkingHours: {},
      updateSchedulerHoursTimeout: null,
      showUrgentTaskModal: false,
      urgentTaskData: {},
      taskById: new Map(),
      indexByTaskId: new Map(),
      tasksByWorkProcess: new Map(),
      nonWorkingCellsCache: new Set(),
      timesheetLookup: new Map(),
      appliedStartHour: null,
      appliedEndHour: null,
      pendingConnectionsDraw: false,
      overlappingTaskIds: new Set(),
    };
  },
  computed: {
    ...mapGetters(['getProfile']),
    editingOptions() {
      const profile = this.getProfile;
      if (!profile || !profile.name) return null;
      return {
        allowAdding: PermissionCheck.can('workplanner.create_task'),
        allowUpdating: PermissionCheck.can('workplanner.edit_task'),
        allowDeleting: PermissionCheck.can('workplanner.delete_task'),
        allowDragging: PermissionCheck.can('workplanner.edit_task'),
        allowResizing: PermissionCheck.can('workplanner.edit_task'),
      };
    },
    schedulerCellDuration() {
      return settings.schedulerCellDurationMinutes;
    },
    schedulerDefaultStartDayHour() {
      return settings.schedulerDefaultStartDayHour;
    },
    schedulerDefaultEndDayHour() {
      return settings.schedulerDefaultEndDayHour;
    },
    // Permission checks - made reactive by depending on getProfile from Vuex
    canViewWorkPlanner() {
      // Access getProfile to create reactive dependency that re-evaluates when user data loads
      const profile = this.getProfile;
      if (!profile) return false;
      return PermissionCheck.can('workplanner.view');
    },
    canCreateTask() {
      const profile = this.getProfile;
      if (!profile) return false;
      return PermissionCheck.can('workplanner.create_task');
    },
    canEditTask() {
      const profile = this.getProfile;
      if (!profile) return false;
      return PermissionCheck.can('workplanner.edit_task');
    },
    canDeleteTask() {
      const profile = this.getProfile;
      if (!profile) return false;
      return PermissionCheck.can('workplanner.delete_task');
    },
    canToggleComplete() {
      const profile = this.getProfile;
      if (!profile) return false;
      return PermissionCheck.can('workplanner.toggle_complete');
    },
    canManageFilters() {
      const profile = this.getProfile;
      if (!profile) return false;
      return PermissionCheck.can('workplanner.manage_filters');
    },
    filteredUsersData() {
      return this.selectedUserIds.length === 0
          ? this.usersData
          : this.usersData.filter(user => this.selectedUserIds.includes(user.id));
    },
    dynamicStartDayHour() {
      if (!this.timesheetData || this.timesheetData.length === 0) {
        return this.schedulerDefaultStartDayHour;
      }

      // Use currentView to determine if day or week view
      if (this.currentView === 'timelineDay') {
        // For day view: get the earliest start for the current day only
        const dateKey = this.formatDate(this.$refs.scheduler.instance.getStartViewDate());
        const dayHours = this.perDayWorkingHours[dateKey];

        if (dayHours && dayHours.minHour < 24) {
          return Math.floor(dayHours.minHour);
        }
        return this.schedulerDefaultStartDayHour;
      } else {
        // For week view: get the earliest start across all days in the week
        let minHour = this.schedulerDefaultStartDayHour;

        Object.values(this.perDayWorkingHours).forEach(dayData => {
          if (dayData.minHour < minHour) {
            minHour = dayData.minHour;
          }
        });

        return Math.floor(minHour);
      }
    },
    dynamicEndDayHour() {
      if (!this.timesheetData || this.timesheetData.length === 0) {
        return this.schedulerDefaultEndDayHour;
      }

      // Use currentView to determine if day or week view
      if (this.currentView === 'timelineDay') {
        // For day view: get the latest end for the current day only
        const dateKey = this.formatDate(this.$refs.scheduler.instance.getStartViewDate());
        const dayHours = this.perDayWorkingHours[dateKey];

        if (dayHours && dayHours.maxHour > 0) {
          return Math.ceil(dayHours.maxHour);
        }
        return this.schedulerDefaultEndDayHour;
      } else {
        // For week view: get the latest end across all days in the week
        let maxHour = this.schedulerDefaultEndDayHour;

        Object.values(this.perDayWorkingHours).forEach(dayData => {
          if (dayData.maxHour > maxHour) {
            maxHour = dayData.maxHour;
          }
        });

        return Math.ceil(maxHour);
      }
    },
  },
  async mounted() {
    await this.loadUsers();
    await this.loadPendingTasks();
    this.scrollToCurrentTime();
  },
  created() {
    this.translateSchedulerToSlovenian();
  },
  beforeDestroy() {
    this.removeEventListenersFromTasks();
  },
  methods: {
    ...mapActions('workProcesses', ['deleteTask']),
    openCreateTaskPopup() {
      const now = new Date();
      const start = new Date(now);
      const minutes = start.getMinutes();

      const remainder = minutes % this.schedulerCellDuration;
      const minutesToAdd = remainder === 0
          ? this.schedulerCellDuration
          : (this.schedulerCellDuration - remainder);
      start.setMinutes(minutes + minutesToAdd, 0, 0);

      const end = new Date(start);
      end.setMinutes(end.getMinutes() + this.schedulerCellDuration);

      // Format datetime for input fields
      const formatForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const mins = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${mins}`;
      };

      this.urgentTaskData = {
        name: '',
        description: '',
        started_at: formatForInput(start),
        completed_at: formatForInput(end),
        default_user_id: this.selectedUserIds.length > 0 ? this.selectedUserIds[0] : null,
        is_urgent: true,
        autoassign: false,
        is_time_locked: false,
        workProcess: null,
        sp_orders_data: []
      };

      this.showUrgentTaskModal = true;
    },
    editPendingTask(task) {
      // Prepare task data for editing
      this.urgentTaskData = {
        id: task.id,
        name: task.name,
        description: task.description || '',
        time: task.time,
        default_user_id: task.default_user_id,
        role_id: task.role_id,
        role: task.role?.name || null,
        is_worker_free: task.is_worker_free,
        workProcess: task.work_process,
        sp_orders_data: task.sp_orders_data || [],
        work_processes_id: task.work_processes_id
      };

      this.showUrgentTaskModal = true;
    },
    async handleTaskSubmit(payload) {
      try {
        if (payload.id) {
          await SchedulerService.updateTask({
            workProcessId: payload.work_processes_id,
            taskId: payload.id,
            data: payload,
          });
          this.$refs?.toastr?.s('Uspeh', 'Naloga je bila uspešno posodobljena.');
          await this.loadPendingTasks();
          if (payload.scheduling_mode !== 'pending') {
            this.$refs.scheduler.instance.getDataSource().reload().then(() => this.drawConnections());
          }
        } else {
          await SchedulerService.createQuickTask({ data: payload });
          this.$refs?.toastr?.s('Uspeh', 'Naloga je bila uspešno ustvarjena.');
          await this.onScheduleSuccess();
        }
        this.closeUrgentTaskModal();
      } catch (error) {
        const errorMessage = error?.response?.data?.error || error.message || 'Prišlo je do napake.';
        this.$refs?.toastr?.e('Napaka!', errorMessage);
      }
    },
    closeUrgentTaskModal() {
      this.showUrgentTaskModal = false;
      this.urgentTaskData = {};
    },
    formatDatetime(date) {
      return format(new Date(date), 'yyyy-MM-dd HH:mm:ss');
    },
    formatDate(date) {
      return format(new Date(date), 'yyyy-MM-dd');
    },
    formatTime(dateTime) {
      return format(new Date(dateTime), 'HH:mm');
    },
    getHourFromTimestamp(timestamp) {
      if (!timestamp) return 0;
      const date = new Date(timestamp);
      return date.getHours() + (date.getMinutes() / 60);
    },
    parseTask(task) {
      let spOrdersDataDisplayText = '';
      let spOrdersData = [];
      if (task.work_process.sp_orders.length > 0) {
        for (let i=0; i<task.work_process.sp_orders.length; i++) {
          const spOrder = task.work_process.sp_orders[i];
          const spOrderNumber = spOrder?.sp_order_number ?? "/";
          const spOrderClientFirstName = spOrder?.client?.first_name ?? "";
          const spOrderClientLastName = spOrder?.client?.last_name ?? "";
          const spOrderClientName = spOrderClientFirstName || spOrderClientLastName ? `${spOrderClientFirstName} ${spOrderClientLastName}` : "/";
          const spOrderVehicleDataBrand = spOrder?.vehicle?.brand?.title ?? spOrder?.brand?.title ?? "";
          const spOrderVehicleDataModel = spOrder?.vehicle?.model ?? spOrder?.model ?? "";
          const spOrderVehicleDataRegistrationNumber = spOrder?.vehicle?.registration_number ?? spOrder?.registration_number ?? "";
          const spOrderVehicleData = (spOrderVehicleDataBrand || spOrderVehicleDataModel || spOrderVehicleDataRegistrationNumber) ? `${spOrderVehicleDataBrand} ${spOrderVehicleDataModel} ${spOrderVehicleDataRegistrationNumber}` : "/";

          const spOrderData = {
            id: spOrder.id,
            number: spOrderNumber,
            client_name: spOrderClientName,
            vehicle_data: spOrderVehicleData
          };
          spOrdersDataDisplayText += `Naročilo: ${spOrderData.number}`;
          spOrdersDataDisplayText += " | ";
          spOrdersDataDisplayText += `Stranka: ${spOrderData.client_name}`;
          spOrdersDataDisplayText += " | ";
          spOrdersDataDisplayText += `Vozilo: ${spOrderData.vehicle_data}`;
          spOrdersData.push(spOrderData);
        }
      }

      let displayText = [];
      if (task.name) {
        displayText.push(task.name);
      }
      if (spOrdersDataDisplayText) {
        displayText.push(spOrdersDataDisplayText);
      }
      if (task.description) {
        displayText.push(task.description);
      }

      return {
        ...task,
        display_text: displayText.join(" | "),
        sp_orders_data: spOrdersData,
      }
    },
    getTaskColor(taskId) {
      const task = this.taskById.get(taskId);
      if (!task) return null;
      if (task.completed) return '#4CAF50';
      if (new Date(task.completed_at).getTime() < Date.now()) return '#ff9800';
      if (this.overlappingTaskIds.has(taskId)) return '#f44336';
      return '#2196F3';
    },
    buildTaskIndexes() {
      this.taskById.clear();
      this.indexByTaskId.clear();
      this.tasksByWorkProcess.clear();
      this.overlappingTaskIds.clear();

      for (let i = 0; i < this.scheduledTasks.length; i++) {
        const task = this.scheduledTasks[i];
        this.taskById.set(task.id, { completed: task.completed, completed_at: task.completed_at });
        this.indexByTaskId.set(task.id, i);

        if (!this.tasksByWorkProcess.has(task.work_processes_id)) {
          this.tasksByWorkProcess.set(task.work_processes_id, []);
        }
        this.tasksByWorkProcess.get(task.work_processes_id).push({
          id: task.id,
          is_worker_free: task.is_worker_free,
          started_at: task.started_at,
          completed_at: task.completed_at,
          completed: task.completed,
        });
      }

      for (const [wpId, tasks] of this.tasksByWorkProcess) {
        tasks.sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
      }

      this.rebuildOverlappingTaskIds();
    },
    rebuildOverlappingTaskIds() {
      this.overlappingTaskIds.clear();

      for (const [wpId, tasks] of this.tasksByWorkProcess) {
        const incomplete = tasks.filter(t => !t.completed && t.started_at && t.completed_at);
        for (let i = 0; i < incomplete.length; i++) {
          const aStart = new Date(incomplete[i].started_at).getTime();
          const aEnd = new Date(incomplete[i].completed_at).getTime();
          for (let j = i + 1; j < incomplete.length; j++) {
            const bStart = new Date(incomplete[j].started_at).getTime();
            const bEnd = new Date(incomplete[j].completed_at).getTime();
            if (aStart < bEnd && aEnd > bStart) {
              this.overlappingTaskIds.add(incomplete[i].id);
              this.overlappingTaskIds.add(incomplete[j].id);
            }
          }
        }
      }
    },
    buildNonWorkingCellsCache() {
      this.timesheetLookup.clear();

      for (const entry of this.timesheetData) {
        this.timesheetLookup.set(`${entry.user_id}|${entry.date}`, { start: entry.start, end: entry.end });
      }

      const scheduler = this.$refs.scheduler?.instance;
      if (!scheduler) return;

      const startViewDate = scheduler.getStartViewDate();
      const endViewDate = scheduler.getEndViewDate();

      const visibleUsers = this.selectedUserIds.length > 0
          ? this.usersData.filter(user => this.selectedUserIds.includes(user.id))
          : this.usersData;

      const cellDurationHours = this.schedulerCellDuration / 60;
      const newCache = new Set();

      for (const user of visibleUsers) {
        let currentDate = new Date(startViewDate);

        while (currentDate <= endViewDate) {
          const dateKey = this.formatDate(currentDate);
          const timesheetEntry = this.timesheetLookup.get(`${user.id}|${dateKey}`);

          if (!timesheetEntry) {
            for (let hour = 0; hour < 24; hour += cellDurationHours) {
              newCache.add(`${user.id}|${dateKey}|${hour}`);
            }
          } else {
            const startHour = this.getHourFromTimestamp(timesheetEntry.start);
            const endHour = this.getHourFromTimestamp(timesheetEntry.end);

            for (let hour = 0; hour < 24; hour += cellDurationHours) {
              if (hour < startHour || hour >= endHour) {
                newCache.add(`${user.id}|${dateKey}|${hour}`);
              }
            }
          }

          currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      this.nonWorkingCellsCache = newCache;
    },
    updateLocalTask(taskId, updatedData) {
      const index = this.indexByTaskId.get(taskId);
      if (index === undefined) return false;

      const oldTask = this.scheduledTasks[index];
      const newTask = { ...oldTask, ...updatedData };

      this.$set(this.scheduledTasks, index, newTask);

      this.taskById.set(taskId, { completed: newTask.completed, completed_at: newTask.completed_at });

      const wpTasks = this.tasksByWorkProcess.get(newTask.work_processes_id);
      if (wpTasks) {
        const idx = wpTasks.findIndex(t => t.id === taskId);
        if (idx !== -1) wpTasks[idx] = {
          id: newTask.id,
          is_worker_free: newTask.is_worker_free,
          started_at: newTask.started_at,
          completed_at: newTask.completed_at,
          completed: newTask.completed,
        };
        wpTasks.sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
      }

      this.rebuildOverlappingTaskIds();

      return true;
    },
    translateSchedulerToSlovenian() {
      loadMessages({
        "sl": {
          "dxScheduler-editorLabelTitle": "Zadeva",
          "dxScheduler-editorLabelStartDate": "Začetni datum",
          "dxScheduler-editorLabelEndDate": "Končni datum",
          "dxScheduler-editorLabelDescription": "Opis",
          "dxScheduler-timelineDay": "Časovnica - Dan",
          "dxScheduler-timelineWeek": "Časovnica - Teden"
        }
      });
      locale("sl");
    },
    async loadUsers() {
      try {
        const requestData = {
          size: 200,
          page: 1,
          sort: '',
          desc: false,
        };
        const responseData = await UsersService.listAll(requestData);
        this.usersData = responseData.users
            .filter(user => user.roles.some(role => ['licar', 'klepar'].includes(role.name)))
            .map(user => ({
              text: user.name,
              id: user.id,
              color: "transparent",
            }));
      } catch (error) {
        console.error('Error fetching users:', error);
        this.$refs.toastr.e(error?.response?.data?.error || error.message || "Med posodabljanjem opravila je prišlo do napake.");
      }
    },
    async loadPendingTasks() {
      try {
        const responseData = await SchedulerService.filterTasks({
          date_from: 'null',
          date_to: 'null',
          is_worker_free: 0
        });
        this.pendingTasks = responseData.tasks.map(task => this.parseTask(task));
      } catch (error) {
        console.error('Error fetching pending tasks:', error);
        this.$refs.toastr.e(error?.response?.data?.error || error.message || "Pri pridobivanju čakajočih opravil je prišlo do napake.");
      }
    },
    scrollToCurrentTime() {
      this.$nextTick(() => {
        const scheduler = this.$refs.scheduler;
        if (!scheduler) return;

        const schedulerInstance = scheduler.instance;
        const scrollable = schedulerInstance.getWorkSpaceScrollable();
        if (!scrollable) return;

        const currentDate = new Date(schedulerInstance.option("currentDate"));
        const firstDayOfWeek = schedulerInstance.option("firstDayOfWeek") || 1;
        const dayOfWeek = currentDate.getDay();
        const daysToSubtract = (dayOfWeek - firstDayOfWeek + 7) % 7;

        const startDate = new Date(currentDate);
        startDate.setDate(currentDate.getDate() - daysToSubtract);
        startDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const daysDifference = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));

        if (daysDifference < 0) return;

        const cellWidth = schedulerInstance.$element()
            .find(".dx-scheduler-date-table-cell")
            .first()
            .outerWidth(true);

        if (!cellWidth) return;

        const scrollLeftPosition = daysDifference * (11 * 2) * cellWidth;
        scrollable.scrollTo({ left: scrollLeftPosition, behavior: "smooth" });
      });
    },
    scrollToAppropriateDay() {
      this.$nextTick(() => {
        const scheduler = this.$refs.scheduler;
        if (!scheduler) return;

        const schedulerInstance = scheduler.instance;
        const scrollable = schedulerInstance.getWorkSpaceScrollable();
        if (!scrollable) return;

        const currentDate = new Date(schedulerInstance.option("currentDate"));
        const firstDayOfWeek = schedulerInstance.option("firstDayOfWeek") || 1;
        const dayOfWeek = currentDate.getDay();
        const daysToSubtract = (dayOfWeek - firstDayOfWeek + 7) % 7;

        // Get the start of the displayed week
        const startDate = new Date(currentDate);
        startDate.setDate(currentDate.getDate() - daysToSubtract);
        startDate.setHours(0, 0, 0, 0);

        // Get today's date
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Check if today is within the displayed week
        const endOfWeek = new Date(startDate);
        endOfWeek.setDate(startDate.getDate() + 6);

        const isCurrentWeek = today >= startDate && today <= endOfWeek;

        let targetDate;
        if (isCurrentWeek) {
          // If it's the current week, scroll to today
          targetDate = today;
        } else {
          // Otherwise, scroll to Monday (first day of week)
          targetDate = startDate;
        }

        // Calculate days difference from start of week to target date
        const daysDifference = Math.floor((targetDate - startDate) / (1000 * 60 * 60 * 24));

        const cellWidth = schedulerInstance.$element()
            .find(".dx-scheduler-date-table-cell")
            .first()
            .outerWidth(true);

        if (!cellWidth) return;

        const scrollLeftPosition = daysDifference * (11 * 2) * cellWidth;
        scrollable.scrollTo({ left: scrollLeftPosition, behavior: "smooth" });
      });
    },
    async handleSchedulingAttempt({ initialData, taskId, onFailure }) {
      try {
        const updatedTask = await this.performTaskUpdate(taskId, initialData);

        if (this.updateLocalTask(taskId, {
          ...initialData,
          started_at: initialData.started_at,
          completed_at: initialData.completed_at,
        })) {
          this.drawConnections();
          await this.loadPendingTasks();
        }

        await this.onScheduleSuccess();

      } catch (error) {
        const errorResponse = error.response;
        if (errorResponse && errorResponse.status === 422 && errorResponse.data.error_type === 'user_unavailable') {
          const confirmationMessage = errorResponse.data.error;
          const question = "\n\nAli želite prestaviti nalogo na ta termin?";

          if (window.confirm(confirmationMessage + question)) {
            const rescheduledData = {
              ...initialData,
              started_at: errorResponse.data.next_available_start,
              completed_at: errorResponse.data.next_available_end,
            };

            try {
              await this.performTaskUpdate(taskId, rescheduledData);
              await this.onScheduleSuccess();
            } catch (rescheduleError) {
              console.error("Error during reschedule attempt:", rescheduleError);
              this.$refs.toastr.e(rescheduleError?.response?.data?.error || rescheduleError.message || "Med prestavljanjem naloge je prišlo do napake.");
              onFailure(rescheduleError);
            }
          } else {
            console.log("Admin cancelled rescheduling.");
            onFailure(error);
          }
        } else {
          console.error('Error during scheduling attempt:', error);
          this.$refs.toastr.e(errorResponse?.data?.error || error.message || "Med posodabljanjem opravila je prišlo do napake.");
          this.$refs.scheduler.instance.getDataSource().reload();
          onFailure(error);
        }
      }
    },

    async performTaskUpdate(taskId, data) {
      const values = { ...data };

      // Normalize role payload
      values.role = values.role?.name || values.role;

      // Respect direct edits from the Scheduler (resize/drag) where started_at/completed_at are provided
      const hasStart = !!values.started_at;
      const hasEnd = !!values.completed_at && (values.completed_at !== values.started_at);
      const hasTime = values.time != null;

      if (hasStart && values.started_at instanceof Date) {
        values.started_at = this.formatDatetime(values.started_at);
      }
      if (hasEnd && values.completed_at instanceof Date) {
        values.completed_at = this.formatDatetime(values.completed_at);
      }

      // If both dates are present, derive time from their difference
      if (hasStart && hasEnd) {
        const startDate = new Date(values.started_at);
        const endDate = new Date(values.completed_at);
        const diffHours = Math.max(0, (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60));
        values.time = +diffHours.toFixed(2);
      } else if (hasStart && hasTime && !hasEnd) {
        // If only start and time provided, compute end
        const computedEnd = new Date(values.started_at);
        computedEnd.setMinutes(computedEnd.getMinutes() + (+values.time * 60));
        values.completed_at = this.formatDatetime(computedEnd);
      }

      values.is_updating_in_calendar = 1;

      await SchedulerService.updateTask({
        workProcessId: values.work_processes_id,
        taskId: taskId,
        data: values
      });
    },

    async onScheduleSuccess() {
      this.$refs.scheduler.instance.getDataSource().reload().then(() => this.drawConnections());
      await this.loadPendingTasks();
    },

    async onAppointmentAdd(e) {
      let taskData = e.itemData;
      taskData.completed_at = null;
      await this.handleSchedulingAttempt({
        initialData: taskData,
        taskId: e.itemData.id,
        onFailure: () => {
          this.$refs.scheduler.instance.getDataSource().reload();
        }
      });
    },

    onListDragStart(e) {
      e.cancel = true;
    },
    onItemDragStart(e) {
      if (!this.canEditTask) {
        e.cancel = true;
        return;
      }
      e.itemData = e.fromData;
    },
    onItemDragEnd(e) {
      if (e.toData) {
        e.cancel = true;
      }
    },
    onCellClick(e) {
      if (!e.appointmentData) {
        e.cancel = true;
      }
    },
    onContentReady(e) {
      if (this.pendingConnectionsDraw) {
        this.pendingConnectionsDraw = false;
        this.drawConnections();
      }
      this.markEndOfDayCells();
      this.attachEventListenersOnTasks();
    },
    markEndOfDayCells() {
      const markers = this.$el.querySelectorAll('[data-eod="1"]');
      markers.forEach(el => el.parentElement.classList.add('end-of-day-cell'));

      const lastSlotMinutes = ((this.appliedEndHour ?? 16) * 60) - this.schedulerCellDuration;
      const hh = String(Math.floor(lastSlotMinutes / 60)).padStart(2, '0');
      const mm = String(lastSlotMinutes % 60).padStart(2, '0');
      this.$el.querySelectorAll(`.dx-scheduler-header-panel-cell[title="${hh}:${mm}"]`)
        .forEach(cell => cell.classList.add('end-of-day-cell'));
    },
    updateSchedulerHours() {
      if (this.updateSchedulerHoursTimeout) {
        clearTimeout(this.updateSchedulerHoursTimeout);
      }

      this.updateSchedulerHoursTimeout = setTimeout(() => {
        this.$nextTick(() => {
          const scheduler = this.$refs.scheduler?.instance;
          if (!scheduler) return;

          const newStartHour = this.dynamicStartDayHour;
          const newEndHour = this.dynamicEndDayHour;

          if (this.appliedStartHour === newStartHour && this.appliedEndHour === newEndHour) {
            return;
          }

          const scrollable = scheduler.getWorkSpaceScrollable();
          const scrollableEl = scrollable ? scrollable.$element()[0].querySelector('.dx-scrollable-container') : null;
          let scrollLeftPercent = 0;
          let scrollTopPercent = 0;

          if (scrollableEl) {
            const maxScrollLeft = scrollableEl.scrollWidth - scrollableEl.clientWidth;
            const maxScrollTop = scrollableEl.scrollHeight - scrollableEl.clientHeight;
            scrollLeftPercent = maxScrollLeft > 0 ? scrollableEl.scrollLeft / maxScrollLeft : 0;
            scrollTopPercent = maxScrollTop > 0 ? scrollableEl.scrollTop / maxScrollTop : 0;
          }

          this.appliedStartHour = newStartHour;
          this.appliedEndHour = newEndHour;

          this.pendingConnectionsDraw = true;
          scheduler.option({ startDayHour: newStartHour, endDayHour: newEndHour });
          scheduler.repaint();

          this.$nextTick(() => {
            const scrollableAfter = scheduler.getWorkSpaceScrollable();
            if (scrollableAfter && scrollableEl) {
              const scrollableElAfter = scrollableAfter.$element()[0].querySelector('.dx-scrollable-container');
              const maxScrollLeftAfter = scrollableElAfter.scrollWidth - scrollableElAfter.clientWidth;
              const maxScrollTopAfter = scrollableElAfter.scrollHeight - scrollableElAfter.clientHeight;
              scrollableAfter.scrollTo({
                left: maxScrollLeftAfter * scrollLeftPercent,
                top: maxScrollTopAfter * scrollTopPercent,
              });
            }
          });
        });
      }, 100);
    },
    showAllUsers() {
      this.selectedUserIds = [];
      this.$refs.scheduler.instance.getDataSource().reload();
    },
    toggleUserFilter(userId) {
      const index = this.selectedUserIds.indexOf(userId);
      if (index > -1) {
        this.selectedUserIds.splice(index, 1);
      } else {
        this.selectedUserIds.push(userId);
      }

      this.$refs.scheduler.instance.getDataSource().reload();
    },
    drawConnections() {
      const container = document.querySelector(".dx-scheduler .dx-scheduler-date-table-scrollable .dx-scrollable-content");
      if (!container) return;

      this.clearLines(container);

      const taskElements = {};
      document.querySelectorAll(".dx-scheduler-appointment-content").forEach(el => {
        const taskId = el.getAttribute("data-id");
        if (taskId) taskElements[taskId] = el;
      });

      for (const [workProcessId, tasks] of this.tasksByWorkProcess) {
        for (let i = 0; i < tasks.length - 1; i++) {
          const currentTask = tasks[i];
          const nextTask = tasks[i + 1];

          if (!currentTask.is_worker_free && !nextTask.is_worker_free
              && taskElements[currentTask.id] && taskElements[nextTask.id]) {
            this.connectElementsWithStraightLine(container, taskElements[currentTask.id], taskElements[nextTask.id]);
          }
        }
      }
    },
    connectElementsWithStraightLine(container, element1, element2) {
      const box1 = element1.getBoundingClientRect();
      const box2 = element2.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      let svg = container.querySelector("svg.connection-lines");
      if (!svg) {
        svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.classList.add("connection-lines");
        svg.style.position = "absolute";
        svg.style.top = "0";
        svg.style.left = "0";
        svg.style.width = "100%";
        svg.style.height = "100%";
        svg.style.pointerEvents = "none";
        svg.style.zIndex = 1;
        container.appendChild(svg);
      }

      const startX = box1.right - containerRect.left;
      const startY = box1.top + box1.height / 2 - containerRect.top;

      const endX = box2.left - containerRect.left;
      const endY = box2.top + box2.height / 2 - containerRect.top;

      let d;

      if (Math.abs(startY - endY) < 3) {
        d = `M ${startX} ${startY} L ${endX} ${endY}`;
      } else {
        const midX1 = startX + 5;
        const midY1 = startY;
        const midX2 = midX1;
        const midY2 = (startY + endY) / 2;
        const midX3 = endX - 5;
        const midY3 = midY2;
        const midX4 = midX3;
        const midY4 = endY;

        d = `
          M ${startX} ${startY}
          C ${startX} ${startY}, ${midX1} ${midY1}, ${midX1} ${midY1}
          L ${midX2} ${midY2}
          C ${midX2} ${midY2}, ${midX3} ${midY3}, ${midX3} ${midY3}
          L ${midX4} ${midY4}
          C ${midX4} ${midY4}, ${endX} ${endY}, ${endX} ${endY}
        `;
      }

      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", d);
      path.setAttribute("stroke", "black");
      path.setAttribute("stroke-width", 2);
      path.setAttribute("fill", "none");
      path.setAttribute("data-work-process-id", element1.getAttribute("data-work-process-id"));
      path.style.opacity = '0.5';

      svg.appendChild(path);
    },
    clearLines(container) {
      const svg = container.querySelector("svg.connection-lines");
      if (svg) {
        svg.innerHTML = "";
      }
    },
    onAppointmentFormOpening(e) {
      e.cancel = true;

      if (!this.canEditTask || !e.appointmentData?.id) return;

      const appt = e.appointmentData;
      this.urgentTaskData = {
        id: appt.id,
        name: appt.name,
        description: appt.description || '',
        time: appt.time,
        default_user_id: appt.default_user_id,
        default_user: appt.user || null,
        role_id: appt.role_id,
        role: appt.role || null,
        is_worker_free: appt.is_worker_free,
        workProcess: appt.work_process || null,
        sp_orders_data: appt.sp_orders_data || [],
        work_processes_id: appt.work_processes_id,
        earliest_start_at: appt.earliest_start_at || null,
        started_at: appt.started_at || null,
        completed_at: appt.completed_at || null,
      };
      this.showUrgentTaskModal = true;
    },
    onOptionChanged(e) {
      if (e.name === 'currentView') {
        this.$refs.scheduler.instance.getDataSource().reload().then(() => {
          this.updateSchedulerHours();
        });
        this.scrollToCurrentTime();
      } else if (e.name === 'currentDate') {
        this.updateSchedulerHours();
        this.scrollToAppropriateDay();
      }
    },
    onAppointmentContextMenu(e) {
      const { appointmentData } = e;

      if (e.event) {
        e.event.preventDefault();
      }

      let menuItems;

      // Workers only see toggle complete option
      if (!this.canEditTask && !this.canDeleteTask) {
        menuItems = [
          {
            text: appointmentData.completed ? 'Označi kot nedokončano' : 'Označi kot dokončano',
            onItemClick: () => this.toggleCompletionStatusOnTask(appointmentData),
            visible: this.canToggleComplete,
          },
        ];
      } else {
        // Full menu for privileged users
        const postponementOptions = [];
        const postponementDate = new Date(appointmentData.started_at);
        let offsetDays = 1;
        let businessDayCount = 1;

        while (postponementOptions.length < 7) {
          postponementDate.setDate(postponementDate.getDate() + 1);
          let dayOfWeek = postponementDate.getDay();

          if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            const offsetDaysParameter = offsetDays;
            postponementOptions.push({
              text: `Preloži za ${businessDayCount} ${businessDayCount === 1 ? 'delovni dan' : 'delovnih dni'}`,
              onItemClick: () => this.postponeWorkProcess(appointmentData.work_processes_id, offsetDaysParameter),
            });
            businessDayCount++;
          }
          offsetDays++;
        }

        menuItems = [
          {
            text: appointmentData.completed ? 'Označi kot nedokončano' : 'Označi kot dokončano',
            onItemClick: () => this.toggleCompletionStatusOnTask(appointmentData),
          },
          {
            text: 'Preloži delovni nalog',
            items: postponementOptions
          },
          {
            text: 'Odstrani nalogo iz koledarja',
            onItemClick: () => this.removeTaskFromCalendar(appointmentData.id),
          },
          {
            text: 'Odstrani proces iz koledarja',
            onItemClick: () => this.removeProcessFromCalendar(appointmentData.work_processes_id),
          },
        ];
      }

      // Update context menu directly via DevExtreme API instead of Vue reactivity
      const contextMenu = this.$refs.contextMenu?.instance;
      if (contextMenu) {
        contextMenu.option('items', menuItems);
      }
    },
    onContextMenuItemClick(e) {
      e.itemData.onItemClick(e);
    },
    async toggleCompletionStatusOnTask(task) {
      try {
        await actions.toggleTaskCompleted(null, {
          taskId: task.id,
          workProcessId: task.work_processes_id
        });
        this.$refs.scheduler.instance.getDataSource().reload();
      } catch (error) {
        console.error('Error setting completion status:', error);
        this.$refs.toastr.e(error?.response?.data?.error || error.message || "Med nastavljanjem statusa dokončanja je prišlo do napake.");
      }
    },
    async postponeWorkProcess(workProcessId, days) {
      try {
        await SchedulerService.postponeWorkProcess(workProcessId, days);
        this.$refs.scheduler.instance.getDataSource().reload().then(() => this.drawConnections());
        await this.loadPendingTasks();
      } catch (error) {
        console.error('Error postponing task:', error);
        this.$refs.toastr.e(error?.response?.data?.error || error.message || "Med odlaganjem opravila je prišlo do napake.");
      }
    },
    async removeTaskFromCalendar(taskId) {
      try {
        await SchedulerService.removeTaskFromCalendar(taskId);
        await this.loadPendingTasks();
        this.$refs.scheduler.instance.getDataSource().reload().then(() => this.drawConnections());
      } catch (error) {
        console.error('Error removing task from calendar:', error);
        this.$refs.toastr.e(error?.response?.data?.error || error.message || "Pri odstranjevanju opravila iz koledarja je prišlo do napake.");
      }
    },
    async removeProcessFromCalendar(workProcessId) {
      try {
        await SchedulerService.removeProcessFromCalendar(workProcessId);
        await this.loadPendingTasks();
        this.$refs.scheduler.instance.getDataSource().reload().then(() => this.drawConnections());
      } catch (error) {
        console.error('Error removing process from calendar:', error);
        this.$refs.toastr.e(error?.response?.data?.error || error.message || "Pri odstranjevanju procesa iz koledarja je prišlo do napake.");
      }
    },
    deletePendingTask(task) {
      if (confirm('Želite izbrisati opravilo?')) {
        this.deleteTask({
          workProcessId: task.work_processes_id,
          taskId: task.id
        })
            .then(() => {
              this.$refs.toastr.s('Success', 'Opravilo izbrisano!')
              this.loadPendingTasks();
            })
            .catch(() => {
              this.$refs.toastr.e('Error', 'Napaka pri brisanju')
            });
      }
    },
    attachEventListenersOnTasks() {
      const scheduler = document.querySelector(".dx-scheduler-scrollable-appointments");
      if (!scheduler) return;
      scheduler.addEventListener("mouseover", this.handleMouseOverOnTask);
      scheduler.addEventListener("mouseout", this.handleMouseOutOnTask);
    },
    removeEventListenersFromTasks() {
      const scheduler = document.querySelector(".dx-scheduler-scrollable-appointments");
      if (!scheduler) return;
      scheduler.removeEventListener("mouseover", this.handleMouseOverOnTask);
      scheduler.removeEventListener("mouseout", this.handleMouseOutOnTask);
    },
    handleMouseOverOnTask(event) {
      const target = event.target.closest("[data-work-process-id]");
      if (target) {
        const workProcessId = target.getAttribute("data-work-process-id");
        this.updateOpacity(workProcessId);
      }
    },
    handleMouseOutOnTask() {
      this.resetOpacity();
    },
    updateOpacity(workProcessId) {
      document.querySelectorAll(".dx-scheduler-appointment-content").forEach(el => {
        const isSameProcess = (el.getAttribute("data-work-process-id") == workProcessId);
        el.style.opacity = isSameProcess ? 1 : 0.6;
        el.style.boxShadow = isSameProcess ? '0 0 5px #333' : null;
      });
      document.querySelectorAll(".connection-lines path").forEach(el => {
        const isSameProcess = (el.getAttribute("data-work-process-id") == workProcessId);
        el.style.opacity = isSameProcess ? 1 : 0.5;
        el.setAttribute("stroke-width", isSameProcess ? 3 : 2);
      });
    },
    resetOpacity() {
      document.querySelectorAll(".dx-scheduler-appointment-content").forEach(el => {
        el.style.opacity = 0.9;
        el.style.boxShadow = null;
      });
      document.querySelectorAll(".connection-lines path").forEach(el => {
        el.style.opacity = 0.5;
        el.setAttribute("stroke-width", 2);
      });
    }
  },
};
</script>

<style>
#wrapper {
  position: relative;
  height: 100%;
}
.c-main > .container-fluid {
  height: 100%;
  padding-bottom: 15px;
}
th.dx-scheduler-header-panel-cell.dx-scheduler-cell-sizes-horizontal,
td.dx-scheduler-date-table-cell {
  max-width: 20px;
  min-width: 20px;
}
th.dx-scheduler-header-panel-cell.dx-scheduler-cell-sizes-horizontal:not(.dx-scheduler-header-panel-week-cell) {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  padding: 5px 2px;
  white-space: nowrap;
}
.dx-scheduler-appointment-content {
  padding: 2px 3px;
  opacity: 0.9;
}
.dx-scheduler-appointment-content .dx-scheduler-appointment-title {
  font-size: 12px;
  line-height: 1.1;
}
.dx-scheduler-appointment-content .dx-scheduler-appointment-content-details {
  margin-top: 0;
  font-size: 10px;
}
.dx-scheduler-timeline.dx-scheduler-work-space-grouped .dx-scheduler-group-row .dx-scheduler-group-header {
  align-items: center;
  font-weight: bold;
  font-size: 13px;
  min-height: 40px !important;
  height: 40px !important;
}
.dx-scheduler-timeline .dx-scheduler-group-header-content {
  line-height: 1.15;
}
.dx-scheduler-work-space.dx-scheduler-timeline .dx-scheduler-date-table-row {
  height: 40px !important;
}
.dx-scheduler-work-space.dx-scheduler-timeline .dx-scheduler-date-table-cell {
  height: 40px !important;
  min-height: 40px !important;
}
.dx-appointment-form-switch.dx-first-row.dx-last-row.dx-field-item.dx-field-item-optional.dx-flex-layout.dx-label-h-align {
  display: none;
}
.dx-tooltip-appointment-item-delete-button-container {
  display: none;
}
.dx-scheduler-appointment {
  z-index: 2;
  padding: 5px 5px;
  border: none !important;
  box-shadow: none !important;
}
#pending-tasks-wrap {
  float: left;
  margin: 25px 0;
  width: 100%;
}
#pending-tasks-wrap h4 {
  color: #000;
  border-bottom: 1px solid #ccc;
  margin-bottom: 10px;
  padding-bottom: 10px;
  font-size: 18px;
}
.dx-draggable-source {
  opacity: 0.5;
}
.dx-draggable-dragging > * {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 6px 8px rgba(0, 0, 0, 0.2);
}
.dx-popup-content a {
  color: #63a0d4;
}
/*
th.dx-scheduler-header-panel-cell.dx-scheduler-header-panel-week-cell,
th.dx-scheduler-header-panel-cell.dx-scheduler-cell-sizes-horizontal[title="15:45"],
td.dx-scheduler-date-table-cell.dx-scheduler-cell-sizes-horizontal.dx-scheduler-cell-sizes-vertical.dx-scheduler-last-group-cell.dx-scheduler-first-group-cell:nth-of-type(32n) {
  border-right: 2px solid #63a0d4 !important;
}
*/
th.dx-scheduler-header-panel-cell.dx-scheduler-header-panel-week-cell {
  border-right: 2px solid #63a0d4 !important;
}
.end-of-day-cell {
  border-right: 2px solid #63a0d4 !important;
}
th.dx-scheduler-header-panel-cell:not(.dx-scheduler-header-panel-week-cell) {
  font-size: 10px !important;
}
.no-pending-tasks-message {
  text-align: center;
  font-size: 14px;
  color: gray;
  font-weight: bold;
}
label[for*="taskDetails"] {
  font-weight: bold;
}
label[for*="timeFormatWarning"] {
  display: none;
}
#list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  grid-auto-rows: auto;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
}
#list .item {
  box-sizing: border-box;
  font-size: 12px;
  background-color: #ffffff;
  padding: 7px 12px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  box-shadow: 0 2px 2px rgba(0,0,0,0.1);
  overflow: visible;
  position: relative;
}
#list .item strong {
  font-size: 14px;
  line-height: 1;
}
.dx-draggable-clone.dx-draggable-dragging {
  opacity: 0.4;
  cursor: grabbing;
}
.dx-scheduler-all-day-table-cell.dx-state-hover, .dx-scheduler-date-table-cell.dx-state-hover {
  background-color: rgba(0, 0, 0, 0.2);
}
.delete-pending-task-button {
  position: absolute;
  right: -5px;
  top: -5px;
  border: 1px solid #999;
  color: #555;
  border-radius: 15px;
  line-height: 12px;
  width: 17px;
  text-align: center;
  padding: 0 0 3px;
  font-size: 15px;
}
.delete-pending-task-button:hover {
  background: #ccc;
}
.edit-pending-task-button {
  position: absolute;
  right: 15px;
  top: -5px;
  border: 1px solid #999;
  color: #555;
  background: #fff;
  border-radius: 15px;
  line-height: 12px;
  width: 17px;
  height: 17px;
  text-align: center;
  padding: 0;
  font-size: 12px;
  cursor: pointer;
}
.edit-pending-task-button:hover {
  background: #e3f2fd;
  border-color: #2196F3;
  color: #2196F3;
}
.non-working-timeslot {
  background-color: #f5f5f5 !important;
  opacity: 0.5 !important;
  pointer-events: none !important;
  position: relative;
  cursor: not-allowed !important;
  background: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 5px,
      rgba(0, 0, 0, 0.2) 5px,
      rgba(0, 0, 0, 0.2) 10px
  );
}
.user-filter {
  padding: 10px;
  border: 1px solid #ddd;
  background: #fafafa;
  width: 100%;
  font-size: 14px;
}
.user-filter-item {
  padding: 6px 10px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
  display: inline-block;
}
.user-filter-item:hover {
  background-color: #c1d75145;
}
.user-filter-item.selected {
  background-color: #c1d751;
  color: white;
}
</style>
